import { useRef, useState, type PointerEvent } from 'react';
import { animate, useMotionValue, useTransform } from 'motion/react';
import {
  CATCH_SNAP_MS,
  DRAG_POWER,
  DRAG_TIME_CONSTANT,
} from './readyStep.constants';

export type LaneDragPhase = 'idle' | 'dragging' | 'settling';

/**
 * Wrap an unbounded drag offset into (-step/2, step/2]. Every tile shows
 * the same pack image, so shifting the strip by a whole number of tile
 * steps is invisible — this is what lets the finite 91-tile lane scroll
 * forever in both directions without constraints or a visible re-base.
 */
export function wrapLaneX(v: number, step: number): number {
  if (step <= 0) return 0;
  return v - Math.round(v / step) * step;
}

/**
 * Manual flick on the slot lane — the player can scroll the reel with a
 * finger / mouse instead of pressing Shuffle, stop on a pack and tap it.
 *
 * Mechanics:
 *   • The gesture writes to the UNBOUNDED value `x` via `_dragX`, so the
 *     drag host itself never transforms. The strip is moved by `wrappedX`
 *     (`x` wrapped modulo one tile step) on the inner wrapper — the lane
 *     visually stays within half a tile of centre while the raw offset
 *     grows freely, which is what makes the scroll infinite.
 *   • Inertia (`dragTransition`) glides the raw value; `modifyTarget`
 *     snaps it to a whole tile step, so `wrappedX` always settles at 0 —
 *     the reel never rests between cards and needs no visible re-base.
 *   • CATCHING the reel: pointer-down freezes Motion's inertia, and if
 *     the press then releases without travelling the drag threshold,
 *     Motion fires NO drag callbacks — the lane would freeze between
 *     tiles forever. The pointer handlers below track that press
 *     lifecycle themselves and run a corrective snap to the nearest
 *     tile on release (`settleToGrid`).
 *   • `phaseRef` mirrors `phase` synchronously: the browser dispatches
 *     `click` right after the pointerup that ends a drag or a catch,
 *     BEFORE React re-renders with the updated state — so tap / Shuffle
 *     guards must read the ref, not the state value.
 */
export function useLaneDrag({
  tileStep,
  enabled,
}: {
  /** Centre-to-centre tile distance in px (0 while unmeasured → drag off). */
  tileStep: number;
  /** Dragging is only offered while the lane is in `idle` mode. */
  enabled: boolean;
}) {
  const x = useMotionValue(0);
  const [phase, setPhase] = useState<LaneDragPhase>('idle');
  const phaseRef = useRef<LaneDragPhase>('idle');
  // Press lifecycle owned by us, NOT Motion: `pressRef` is true while a
  // pointer is down on the lane; `dragStartedRef` flips when Motion's
  // drag actually begins for that press (≥ drag-start threshold moved).
  const pressRef = useRef(false);
  const dragStartedRef = useRef(false);

  // Wrapping runs on every drag frame — read the freshest measurement via
  // a ref so a container-query resize mid-gesture doesn't use a stale step.
  const stepRef = useRef(tileStep);
  stepRef.current = tileStep;
  const wrappedX = useTransform(x, (v) => wrapLaneX(v, stepRef.current));

  const toPhase = (next: LaneDragPhase) => {
    phaseRef.current = next;
    setPhase(next);
  };

  const finishAtGrid = () => {
    // The raw offset is a whole number of steps here (± the animation's
    // rest delta), so zeroing it doesn't visibly move `wrappedX`.
    x.set(0);
    toPhase('idle');
  };

  // Corrective snap after a catch-release: glide the lane to the nearest
  // tile centre. Guards re-checks `pressRef` on completion because a new
  // catch stops this animation (its promise still resolves) and that
  // press then owns the settle.
  const settleToGrid = () => {
    const step = stepRef.current;
    const current = x.get();
    const target = step > 0 ? Math.round(current / step) * step : 0;
    toPhase('settling');
    if (Math.abs(current - target) < 1) {
      // Already on grid — still defer the unlock past the click that
      // follows this pointerup, so catching the reel exactly on a pack
      // doesn't count as a tap-to-open.
      requestAnimationFrame(() => {
        if (!pressRef.current) finishAtGrid();
      });
      return;
    }
    animate(x, target, {
      duration: CATCH_SNAP_MS / 1000,
      ease: 'easeOut',
    }).then(() => {
      if (pressRef.current) return;
      finishAtGrid();
    });
  };

  // Handlers stay attached even when `drag` flips off so an in-flight
  // glide can always finish its settle cycle.
  const dragProps = {
    drag: enabled && tileStep > 0 ? ('x' as const) : false,
    // Route the gesture into our raw value instead of the host's own
    // transform — the visual movement comes from `wrappedX` alone.
    _dragX: x,
    dragTransition: {
      power: DRAG_POWER,
      timeConstant: DRAG_TIME_CONSTANT,
      // Land the glide exactly on a tile centre.
      modifyTarget: (target: number) =>
        tileStep > 0 ? Math.round(target / tileStep) * tileStep : target,
    },
    onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
      if (!enabled || tileStep <= 0) return;
      pressRef.current = true;
      dragStartedRef.current = false;
      if (phaseRef.current !== 'idle') {
        // Catching a moving reel: block tap-to-open for this press and
        // retarget the release to the lane, so a catch that slips off
        // the strip by a couple of px still gets its corrective snap.
        // No capture on a resting press — capture would retarget the
        // follow-up `click` away from the tile and break tap-to-open.
        toPhase('dragging');
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }
    },
    onPointerUp: () => {
      if (!pressRef.current) return;
      pressRef.current = false;
      // A real drag happened — Motion's momentum + modifyTarget own the
      // settle (handled via onDragEnd / onDragTransitionEnd below).
      if (dragStartedRef.current) return;
      // Plain press on a resting lane — nothing to correct; the
      // follow-up click is a legit tap-to-open.
      if (phaseRef.current === 'idle') return;
      settleToGrid();
    },
    onPointerCancel: () => {
      if (!pressRef.current) return;
      pressRef.current = false;
      if (dragStartedRef.current) return;
      if (phaseRef.current === 'idle') return;
      settleToGrid();
    },
    onDragStart: () => {
      dragStartedRef.current = true;
      toPhase('dragging');
    },
    onDragEnd: () => {
      // Mouse releases can land outside the lane (Motion listens on
      // window) — close the press here too.
      pressRef.current = false;
      toPhase('settling');
    },
    onDragTransitionEnd: () => {
      // A new catch may stop the momentum animation, which still
      // resolves it — that press owns the settle then, not us.
      if (pressRef.current) return;
      finishAtGrid();
    },
  };

  return { wrappedX, phase, phaseRef, dragProps };
}
